import { Dispatch, SetStateAction, useEffect } from 'react';
import { Col, Row } from 'antd5';
import { Button } from 'components/ui';
import {
  AtlasGqlInspectionType,
  AtlasGqlMetadataField,
  AtlasGqlMetadataFieldMapping,
} from 'types/atlas-graphql';
import { Steps } from '../UploadInspectionDataModal';
import { ButtonContainer, LabelColumn } from '../UploadInspectionDataModal.style';
import { useUploadContext } from 'components/FileUploader';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';

type PreviewProps = {
  inspectionCount: number;
  inspectionType?: AtlasGqlInspectionType;
  internalFields: AtlasGqlMetadataField[];
  fieldMappings: AtlasGqlMetadataFieldMapping[];
  records: { [key: string]: string }[];
  setStep: Dispatch<SetStateAction<Steps>>;
  handleComplete: () => void;
};

export const Preview: React.FC<PreviewProps> = ({
  inspectionCount,
  inspectionType,
  internalFields,
  fieldMappings,
  records,
  setStep,
  handleComplete,
}) => {
  const { uploading } = useUploadContext();
  const { setAlertState } = useUploadInspectionDataModalContext();

  const inspectionTypeFieldName = fieldMappings.find(fieldMapping => {
    const internalTypeField = internalFields.find(field => field.displayName === 'Inspection Type');
    return fieldMapping.targetFieldName === internalTypeField?.internalName;
  });
  const inspectionTypes = inspectionTypeFieldName
    ? records.map(record => record[inspectionTypeFieldName.sourceFieldName])
    : [];

  const isValid = inspectionType?.name
    ? inspectionTypes.every(type => inspectionType?.name === type)
    : inspectionTypes.every(type => inspectionTypes[0] === type);

  const previewRecord = records[0];

  useEffect(() => {
    if (!isValid) {
      setAlertState({
        alertTitle: `All Inspections must be of the same type ${inspectionType?.name ? `: ${inspectionType?.name}` : '.'}`,
        alertType: 'error',
        alertDescription: 'Please use the button below to upload again.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const goBack = (step: Steps) => {
    setAlertState(null);
    setStep(step);
  };

  return (
    <>
      {isValid && (
        <>
          <p>
            This will create {inspectionCount} inspection{inspectionCount === 1 ? '' : 's'}.
          </p>
          <p>Preview of the first uploaded inspection:</p>
          {fieldMappings.map(({ sourceFieldName, targetFieldName }) => (
            <Row key={targetFieldName}>
              <Col flex="10%" />
              <LabelColumn flex="auto">
                <label>
                  {internalFields.find(field => field.internalName === targetFieldName)
                    ?.displayName ?? targetFieldName}
                  :
                </label>
              </LabelColumn>
              <Col flex="60%">{previewRecord[sourceFieldName]}</Col>
            </Row>
          ))}
        </>
      )}

      <ButtonContainer>
        {isValid ? (
          <>
            <Button
              onClick={() => {
                goBack(Steps.SummaryMapping);
              }}
            >
              Back
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setAlertState(null);
                handleComplete();
              }}
              loading={uploading}
            >
              Continue
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                goBack(Steps.SummaryUpload);
              }}
            >
              Reupload
            </Button>
          </>
        )}
      </ButtonContainer>
    </>
  );
};
